/**
 * What an account actually has overdue, bucket by bucket.
 *
 * Ported unchanged from the reference implementation, where it was arrived at
 * the hard way. Every figure a receivables sheet gives is an absolute amount
 * with its Dr/Cr type in a sibling column — the total, each ageing bucket, and
 * the sheet's own "over 90" and "due > 45" roll-ups. Read without the type, a
 * credit note that is 135 days old becomes "money 135 days overdue", and an
 * account whose whole balance is an advance we are holding becomes "past 90
 * days". Most of one book's credit accounts sat in the >90d filter for exactly
 * that reason, and they were what the ">90d" list showed as "Good" customers.
 *
 * So: an account in credit overall has nothing overdue. Otherwise each bucket
 * is signed — debit positive, credit negative — and a window nets out, which is
 * how a sheet's own roll-ups are computed. The sheet's roll-ups are used where
 * it supplies them, honouring their type; the buckets are netted otherwise.
 * Nothing here is ever negative: a window that nets to credit is simply nothing
 * overdue.
 */
import type { AgeingBucket, BalanceType, Customer } from './customer.js';

export interface OverdueAgeing {
    /** Debit in each bucket, net of any credit in the same bucket. */
    a1: number;
    a2: number;
    a3: number;
    a4: number;
    /** Net receivable older than 45 / 90 / 135 days. */
    over45: number;
    over90: number;
    over135: number;
}

const NOTHING_OVERDUE: OverdueAgeing = { a1: 0, a2: 0, a3: 0, a4: 0, over45: 0, over90: 0, over135: 0 };

export type AgeingInput = Pick<
    Customer,
    'ageing' | 'ageingTypes' | 'total' | 'totalType' | 'over90' | 'over90Type' | 'dueOver45' | 'dueOver45Type'
>;

export function overdueAgeing(item: AgeingInput): OverdueAgeing {
    if (item.totalType === 'Cr' || (Number(item.total) || 0) <= 0) return NOTHING_OVERDUE;

    const signed = (key: AgeingBucket): number => {
        const v = Math.abs(Number(item.ageing?.[key]) || 0);
        return item.ageingTypes?.[key] === 'Cr' ? -v : v;
    };
    const s1 = signed('1-45');
    const s2 = signed('46-90');
    const s3 = signed('91-135');
    const s4 = signed('>135');

    const rollUp = (value: number | undefined, type: BalanceType | undefined, fromBuckets: number): number => {
        if (value === undefined || value === null || isNaN(Number(value))) return Math.max(0, fromBuckets);
        return type === 'Cr' ? 0 : Math.max(0, Math.abs(Number(value)));
    };
    const over135 = Math.max(0, s4);
    const over90 = rollUp(item.over90, item.over90Type, s3 + s4);
    const over45 = rollUp(item.dueOver45, item.dueOver45Type, s2 + s3 + s4);

    return {
        a1: Math.max(0, s1),
        a2: Math.max(0, s2),
        a3: Math.max(0, s3),
        a4: over135,
        over45,
        over90,
        over135,
    };
}
